import { Map as MapIcon, SquareCheck, Trophy, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Task } from '@/types/experience';

interface MiniMapModalProps {
  tasks: Task[];
  onClose: () => void;
  zoom: number;
  panX: number;
  panY: number;
  containerW: number;
  containerH: number;
  workspaceContentW: number;
  workspaceContentH: number;
  onPanChange: (panX: number, panY: number) => void;
  onZoomAt: (factor: number, mx: number, my: number) => void;
}

const NODE_W = 52;
const NODE_GAP = 8;
const ZOOM_FACTOR = 1.12;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

function groupByLevel(tasks: Task[]): [number, Task[]][] {
  const map = new globalThis.Map<number, Task[]>();
  for (const t of tasks) {
    const bucket = map.get(t.level) ?? [];
    bucket.push(t);
    map.set(t.level, bucket);
  }
  return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
}

function MiniTaskNode({ task }: { task: Task }) {
  return (
    <div
      className='relative h-11 w-11 rounded-lg border border-ws-border bg-ws-btn p-1'
    >
      {task.points > 0 && (
        <div className='h-full w-full flex'>        
          <div className="flex self-end items-center w-full">
            <div className="h-full flex justify-center items-center">
              <Trophy className='h-[10px] w-[10px] text-ws-muted' />
            </div>
            <div className="text-xs  text-ws-muted  truncate justify-self-end-safe text-right w-full">
              {task.points}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TreeConnector({ childCount }: { childCount: number }) {
  const barWidth = (childCount - 1) * (NODE_W + NODE_GAP);

  return (
    <div className='relative flex justify-center' style={{ height: 28, width: '100%' }}>
      <div className='absolute left-1/2 top-0 h-1/2 w-[2px] -translate-x-px bg-ws-border' />
      {childCount > 1 ? (
        <>
          <div
            className='absolute bg-ws-border'
            style={{ top: '50%', height: 2, width: barWidth, left: '50%', transform: 'translate(-50%, -1px)' }}
          />
          {Array.from({ length: childCount }).map((_, i) => {
            const offset = (i - (childCount - 1) / 2) * (NODE_W + NODE_GAP);
            return (
              <div
                key={i}
                className='absolute bottom-0 w-[2px] bg-ws-border'
                style={{ height: '50%', left: '50%', transform: `translateX(calc(-50% + ${offset}px))` }}
              />
            );
          })}
        </>
      ) : (
        <div className='absolute bottom-0 left-1/2 h-1/2 w-[2px] -translate-x-px bg-ws-border' />
      )}
    </div>
  );
}

export default function MiniMapModal({
  tasks,
  onClose,
  zoom,
  panX,
  panY,
  containerW,
  containerH,
  workspaceContentW,
  workspaceContentH,
  onPanChange,
  onZoomAt,
}: MiniMapModalProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const levels = groupByLevel(tasks);
  const totalPoints = tasks.reduce((sum, t) => sum + t.points, 0);

  const modalRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [mapSize, setMapSize] = useState({ w: 0, h: 0 });

  // Ctrl+scroll → zoom workspace (desktop only)
  useEffect(() => {
    if (isMobile) return;
    const el = modalRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      onZoomAt(e.deltaY < 0 ? ZOOM_FACTOR : 1 / ZOOM_FACTOR, containerW / 2, containerH / 2);
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [isMobile, onZoomAt, containerW, containerH]);

  // Measure the mini-map's rendered tree size
  useEffect(() => {
    const el = graphRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => {
      setMapSize({ w: el.scrollWidth, h: el.scrollHeight });
    });
    obs.observe(el);
    setMapSize({ w: el.scrollWidth, h: el.scrollHeight });
    return () => obs.disconnect();
  }, [tasks]);

  // Viewport rect drag (desktop only)
  const vpDragRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startPanX: 0,
    startPanY: 0,
    scaleX: 1,
    scaleY: 1,
    zoom: 1,
  });
  const prevScrollTopRef = useRef(0);
  const mousePosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (isMobile) return;
    function onMove(e: MouseEvent) {
      mousePosRef.current = { x: e.clientX, y: e.clientY };
      if (!vpDragRef.current.active) return;
      const dx = e.clientX - vpDragRef.current.startX;
      const dy = e.clientY - vpDragRef.current.startY;
      const { scaleX, scaleY, zoom: z, startPanX, startPanY } = vpDragRef.current;
      onPanChange(startPanX - (dx * z) / scaleX, startPanY - (dy * z) / scaleY);
    }
    function onUp() {
      vpDragRef.current.active = false;
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isMobile, onPanChange]);

  // Compensate for mini-map scroll during viewport rect drag (desktop only)
  useEffect(() => {
    if (isMobile) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    function onScroll() {
      if (!vpDragRef.current.active || !el) return;
      const scrollDY = el.scrollTop - prevScrollTopRef.current;
      prevScrollTopRef.current = el.scrollTop;
      vpDragRef.current.startPanY -= (scrollDY * vpDragRef.current.zoom) / vpDragRef.current.scaleY;
      const dx = mousePosRef.current.x - vpDragRef.current.startX;
      const dy = mousePosRef.current.y - vpDragRef.current.startY;
      const { scaleX, scaleY, zoom: z, startPanX, startPanY } = vpDragRef.current;
      onPanChange(startPanX - (dx * z) / scaleX, startPanY - (dy * z) / scaleY);
    }
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [isMobile, onPanChange]);

  // Compute viewport rect in mini-map coords
  const scaleX = mapSize.w > 0 && workspaceContentW > 0 ? mapSize.w / workspaceContentW : 0;
  const scaleY = mapSize.h > 0 && workspaceContentH > 0 ? mapSize.h / workspaceContentH : 0;
  const showViewport = !isMobile && scaleX > 0 && scaleY > 0 && containerW > 0 && containerH > 0;

  const vx = (-panX / zoom) * scaleX;
  const vy = (-panY / zoom) * scaleY;
  const vw = Math.min((containerW / zoom) * scaleX, mapSize.w);
  const vh = Math.min((containerH / zoom) * scaleY, mapSize.h);

  function onVpMouseDown(e: React.MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    prevScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? 0;
    vpDragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startPanX: panX,
      startPanY: panY,
      scaleX,
      scaleY,
      zoom,
    };
  }

  // Click/tap on graph area → jump viewport to that point (centered)
  function onGraphClick(e: React.MouseEvent<HTMLDivElement>) {
    if (!graphRef.current || scaleX === 0 || scaleY === 0) return;
    const rect = graphRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    onPanChange(containerW / 2 - (mx / scaleX) * zoom, containerH / 2 - (my / scaleY) * zoom);
    if (isMobile) onClose();
  }

  const panel = (
    <div
      className={`flex flex-col gap-6 p-6 rounded-xl border w-[328px] border-ws-border bg-ws-elevated shadow-xl ${
        isMobile ? 'relative w-full max-w-sm max-h-[80vh]' : 'h-full w-64'
      }`}
    >
      {/* Header */}
      <div className='flex shrink-0 flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <MapIcon className='h-5 w-5 text-ws-text' strokeWidth={2.5}/>
            <h2 className='text-xl font-semibold text-ws-text'>{t('experience.map.title')}</h2>
          </div>
          <button
            onClick={onClose}
            className="relative flex h-5 w-5 items-center justify-center rounded-md  before:absolute before:h-7 before:w-7 before:rounded-md before:content-[''] hover:before:bg-ws-hover"
            aria-label={t('common.close')}
          >
            <X className="relative z-10 h-3.5 w-3.5 text-ws-muted" />
          </button>
        </div>
        <div className='flex text-xs items-center gap-3'>
          <div className='flex items-center gap-1'>
            <SquareCheck className='h-3 w-3 text-ws-muted' />
            <span className='text-ws-muted'>{tasks.length} tasks</span>
          </div>
          <div className='flex items-center gap-1'>
            <Trophy className='h-3 w-3 text-ws-muted' />
            <span className='text-ws-muted'>{totalPoints} points</span>
          </div>
        </div>
      </div>

      {/* Graph with viewport indicator */}
      <div
        ref={scrollContainerRef}
        className='relative flex-1 rounded-lg border border-ws-border  overflow-y-auto bg-ws-surface'
        onClick={onGraphClick}
      >
        <div ref={graphRef} className='flex flex-col items-center px-4 py-4'>
          {tasks.length === 0 ? (
            <p className='py-8 text-center text-sm text-ws-muted'>{t('experience.map.empty')}</p>
          ) : (
            levels.map(([level, levelTasks], idx) => {
              const sorted = [...levelTasks].sort((a, b) => a.order - b.order);
              const nextCount = levels[idx + 1]?.[1].length ?? 0;
              return (
                <div key={level} className='flex flex-col items-center'>
                  <div className='flex' style={{ gap: NODE_GAP }}>
                    {sorted.map((task) => (
                      <MiniTaskNode key={task.id} task={task} />
                    ))}
                  </div>
                  {nextCount > 0 && <TreeConnector childCount={nextCount} />}
                </div>
              );
            })
          )}
        </div>

        {/* Viewport rect — desktop only */}
        {showViewport && (
          <div
            className='absolute rounded-[16px] border-4 border-ws-secondary'
            style={{
              left: Math.max(0, vx),
              top: Math.max(0, vy),
              width: Math.max(8, vw),
              height: Math.max(8, vh),
              cursor: 'move',
            }}
            onMouseDown={onVpMouseDown}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div ref={modalRef} className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <div className='absolute inset-0 bg-black/50' onClick={onClose} />
        {panel}
      </div>
    );
  }

  return (
    <div ref={modalRef} className='absolute inset-y-0 right-0 z-10 p-2'>
      {panel}
    </div>
  );
}
